/**
 * /pop-ups/pop-up-software/ — content.
 *
 * Fourteenth page off Tilda, and the last of the six that share
 * src/features/pop-ups-common and src/shared/arrays/pop-ups-shared.ts.
 *
 * This one has a clean keyword. "pop up software" is 200/mo US and 300 global
 * at KD 2, and the SERP is commercial: AgileCRM's "Popup Builder Software" and
 * POWR's free popup maker rank as product pages alongside the Wisepops and
 * Sleeknote roundups. "popup software" (150/400, KD 2) and "website popup
 * software" (150/300) are the same cluster and sit in the h2s.
 *
 * Body copy is carried over verbatim from the Tilda export
 * (src/data/tilda/23155609.json), inline links included, minus two pricing
 * claims. The "Affordable price" advantage said the creator "can become a part
 * of both your Business and Automatic plan" — neither plan exists on /pricing/,
 * which now sells Conversation, Support and Marketing — and the cost FAQ said
 * "every Dashly plan, starting at $16/month". The advantage is dropped and the
 * price comes out of the FAQ; the half of that answer that names no plan and
 * the free-trial sentence both stand.
 *
 * Product-KB check: pop-ups are not a capability in the knowledge base at all,
 * so every heading describes only what the page's own copy already describes.
 */

import type { RichText } from "@shared/arrays/pop-ups-shared";

export const heroData = {
  /** The export's h1, kept as the hero lead under the new one. */
  lead: "Smart Pop-up Software to turn your website traffic into subscribers, leads and sales",
  paragraphs: [
    [
      "You need no coding skills to customize pop-ups to capture leads, boost sales, reduce shopping carts, engage visitors instantly with Dashly pop-up software.",
    ],
  ] as RichText[],
  note: ["7 days of free trial. No credit card required"],
  cta: "START FOR FREE",
  image: "/pop-ups-software/hero.avif",
  imageAlt: "Pop-ups built in the Dashly software",
};

export const stepsData = {
  title: "How the popup software works, in five steps",
  /** The export's own h3 and lead-in for this block. */
  lead: "SaaS, eCommerce, blogs, and digital agency websites use Dashly service to customize smart pop-ups for the right people in the right place and time.",
  intro: ["All it takes is five simple steps"] as RichText,
  steps: [
    {
      title: "Customize pop-up design according to your brand style",
      text: [
        [
          "With our drag and drop builder interface, you can change images, background, add video, text, fields, buttons, items in the cart, etc. Pick whatever serves your purpose best.",
        ],
      ] as RichText[],
    },
    {
      title: "Choose the right time",
      text: [
        [
          "When will visitors see the message? For example, when they enter the website for the first time, before leaving it, or after some set time? Decide when you want your ",
          { text: "website message", href: "/pop-ups/" },
          " to show up. Our software allows you to choose any action as a trigger to send your pop-up to visitors.",
        ],
      ] as RichText[],
      image: "/pop-ups-software/icon-step.avif",
      imageAlt: "Trigger settings for a pop-up",
    },
    {
      title: "Define the right people",
      text: [
        [
          "Do you want all the visitors to see your pop-ups? Or do you want them to be targeted to someone specific group of visitors? Specify the audience for your pop-up in Dashly software via lead properties, tags, and events.",
        ],
      ] as RichText[],
    },
    {
      title: "Set the right place",
      text: [
        [
          "Dashly pop-up software allows you to set the list of pages where website visitors will receive the message and where — won't.",
        ],
      ] as RichText[],
    },
    {
      title: "Launch A/B tests to optimize messages and design",
      text: [
        [
          "Use our software to get stats in real-time to improve your lead generation strategy, test new ideas on control groups, and steadily increase conversions.",
        ],
      ] as RichText[],
      image: "/pop-ups-shared/ab-test.avif",
      imageAlt: "Two pop-up variants running as an A/B test",
    },
  ],
  video: "/pop-ups-shared/editor-demo.mp4",
  poster: "/pop-ups-shared/editor-demo-poster.jpg",
  videoAlt: "A pop-up being assembled block by block in the Dashly visual editor",
};

/** Seven tiles. "Feedback pop-up" has no page of its own and stays unlinked. */
export const scenariosData = {
  title: "Ready-made scenarios in the website popup software",
  lead: "Save your time and effort with ready-made scenarios",
  intro: [
    "The choice of a template depends on the kind of interaction you want to have with your visitors. Dashly pop-up software offers a wide range of templates that can come in handy for your business.",
  ] as RichText,
  items: [
    {
      name: "Video pop-up",
      href: "/pop-ups/video-pop-up/",
      image: "/pop-ups-shared/tpl-video-2.avif",
    },
    {
      name: "Lead capture pop-up",
      href: "/pop-ups/lead-capture-pop-up/",
      image: "/pop-ups-shared/tpl-lead-capture-2.avif",
    },
    {
      name: "Survey pop-up",
      href: "/pop-ups/popup-survey/",
      image: "/pop-ups-shared/tpl-survey-2.avif",
    },
    {
      name: "Feedback pop-up",
      href: "",
      image: "/pop-ups-shared/tpl-feedback.avif",
    },
    {
      name: "Email capture pop-up",
      href: "/pop-ups/email-capture-pop-up/",
      image: "/pop-ups-shared/tpl-email.avif",
    },
    {
      name: "Pop-up lightbox",
      href: "/pop-ups/lightbox-pop-up/",
      image: "/pop-ups-shared/tpl-lightbox.avif",
    },
    {
      name: "Exit-intent pop-up",
      href: "/pop-ups/exit-intent-pop-up/",
      image: "/pop-ups-shared/tile-exit.avif",
    },
  ],
};

export const casesHeading = {
  title: "What this popup software did for real teams",
  lead: "Recipes for your company growth with Dashly pop-ups",
};

/**
 * The export listed four advantages. The fourth, "Affordable price", named the
 * Business and Automatic plans, neither of which exists. It is dropped rather
 * than rewritten.
 */
export const advantagesData = {
  title: "Why teams pick this popup software",
  lead: "Why choose Dashly?",
  intro:
    "Our team did their best to create a convenient and accessible pop-up service that can help any business to develop and adjust to customers' desires.",
  items: [
    {
      title: "Straightforward to use",
      text: [
        "Our pop-up software is straightforward to use. You need neither coding nor design skills to create your pop-up from scratch.",
      ] as RichText,
      image: "/pop-ups-shared/adv-editor.avif",
    },
    {
      title: "Flexible settings.",
      text: [
        "Flexible settings. Dashly pop-ups software allows you to customize a template or create pop-ups from scratch to match your brand style.",
      ] as RichText,
      image: "/pop-ups-shared/adv-settings.avif",
    },
    {
      title: "Group work.",
      text: [
        "Group work. Unlike many other pop-up services, Dashly has no limit in agent seats. Two, three, four heads are better than one.",
      ] as RichText,
      image: "/pop-ups-shared/adv-seats.avif",
    },
  ],
};

export const faqData = {
  title: "Popup software FAQ",
  items: [
    {
      q: "What is a pop-up?",
      a: [
        "A pop-up is a short message that shows up on your visitors' screens while they're scrolling through your website. The main goal of these messages is to catch the lead's attention and turn them into your customers eventually. A pop-up can gather some contact information or promote a particular product of yours. The choice of a template entirely depends on your purpose. You can get familiar with all types of pop-ups with the help of our ",
        { text: "pop-up software", href: "/pop-ups/" },
        ".",
      ] as RichText,
      plain:
        "A pop-up is a short message that shows up on your visitors' screens while they're scrolling through your website. The main goal of these messages is to catch the lead's attention and turn them into your customers eventually. A pop-up can gather some contact information or promote a particular product of yours. The choice of a template entirely depends on your purpose. You can get familiar with all types of pop-ups with the help of our pop-up software.",
    },
    {
      q: "What is pop-up software?",
      a: [
        "It's a service that allows you to create your own triggered message from scratch or customize an already-existing template. The settings range of the software is so vast that it includes everything from designing a pop-up to testing new conversion ideas with it. Set triggers to show your pop-up to visitors, choose who sees your message, how, and when it appears on their screens. Our pop-up software lets you create an easy and effective tool for your business development.",
      ] as RichText,
      plain:
        "It's a service that allows you to create your own triggered message from scratch or customize an already-existing template. The settings range of the software is so vast that it includes everything from designing a pop-up to testing new conversion ideas with it. Set triggers to show your pop-up to visitors, choose who sees your message, how, and when it appears on their screens. Our pop-up software lets you create an easy and effective tool for your business development.",
    },
    {
      q: "Do I need any knowledge of coding to create a pop-up?",
      a: [
        "Dashly team created a pop-up software that is friendly to users who are not skilled in coding or design. Our pop-up builder allows you to customize any template, fill in the fields with all necessary information and launch your pop-up in a short time.",
      ] as RichText,
      plain:
        "Dashly team created a pop-up software that is friendly to users who are not skilled in coding or design. Our pop-up builder allows you to customize any template, fill in the fields with all necessary information and launch your pop-up in a short time.",
    },
    {
      // The export answered "every Dashly plan, starting at $16/month". The
      // price no longer matches /pricing/, so it comes out; the rest names no
      // plan and stands.
      q: "How much does it cost?",
      a: [
        "You can add the pop-up service to every Dashly plan. If you're still unsure what plan to choose, you can test our pop-ups software with the 7-days free trial. 5 min installation. No credit card is required.",
      ] as RichText,
      plain:
        "You can add the pop-up service to every Dashly plan. If you're still unsure what plan to choose, you can test our pop-ups software with the 7-days free trial. 5 min installation. No credit card is required.",
    },
  ],
};

export const finalCtaData = {
  title: "Build your first pop-up in the Dashly popup software",
  lead: "Build your first pop-up for free",
  cta: "Get started",
  note: "7 days of free trial. No credit card required",
};
